import * as React from "react";
import { Mail, PhoneOff, ShieldCheck, MessageSquareOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const complianceSections = [{
  id: "opt-out",
  title: "SMS & Phone Opt-Out Preferences",
  icon: PhoneOff,
  description: "We respect your communication preferences. If you no longer wish to receive marketing calls or text messages from Family Legacy Planning, you can easily opt out.",
  instructions: ["Text Messages: Reply 'STOP' to any promotional SMS you receive from us.", "Phone Calls: Inform our representative during a call that you wish to be placed on our Do-Not-Call list.", "Email Request: Send an email to our compliance team with your phone number requesting to be opted out."],
  actionText: "Email Opt-Out Request",
  actionLink: "mailto:optout@familylegacyplanning.net"
}, {
  id: "dpo",
  title: "Data Protection Officer",
  icon: ShieldCheck,
  description: "For specific inquiries regarding how we handle your personal data, privacy concerns, or detailed TCPA compliance questions, please contact our designated Data Protection Officer.",
  instructions: ["Our DPO is responsible for ensuring that our communication practices align with federal and state regulations, including the Telephone Consumer Protection Act."],
  actionText: "Contact DPO",
  actionLink: "mailto:privacy@familylegacyplanning.net"
}, {
  id: "general",
  title: "General Compliance Inquiries",
  icon: Mail,
  description: "If you have general questions about our communication policies, wish to update your contact information, or need assistance managing your preferences, our support team is here to help.",
  instructions: ["Please allow up to 30 days for your contact preferences to be fully updated across all our internal systems."],
  actionText: "Email Compliance Team",
  actionLink: "mailto:compliance@familylegacyplanning.net"
}];
export default function Tcpacompliance() {
  return <section id="tcpacompliance" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <div className="mb-12 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-lg mb-4">
            <MessageSquareOff className="size-6 text-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            TCPA Compliance & Contact Information
          </h2>
          <p className="text-lg text-muted-foreground">
            Family Legacy Planning is committed to respecting your privacy and adhering to the Telephone Consumer Protection Act (TCPA). Below you will find information on how to manage your communication preferences and contact our compliance team.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-3">
          {complianceSections.map((section, index) => <Card key={section.id} data-index={index} className="border border-border shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
              <CardHeader className="pb-4">
                <div className="size-10 rounded-md bg-secondary/20 flex items-center justify-center mb-4">
                  <section.icon className="size-5 text-secondary-foreground" />
                </div>
                <CardTitle className="text-xl font-default text-foreground">
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-muted-foreground mb-6 text-sm leading-relaxed">
                  {section.description}
                </p>
                
                <div className="mb-8 flex-1">
                  <ul className="space-y-3">
                    {section.instructions.map((instruction, i) => <li key={i} data-index={i} className="text-sm text-foreground flex items-start gap-2">
                        <span className="size-1.5 rounded-lg bg-primary mt-1.5 shrink-0" />
                        <span>{instruction}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="mt-auto pt-4 border-t border-border">
                  <Button asChild variant="outline" className="w-full">
                    <Link to={section.actionLink}>
                      <Mail className="size-4 mr-2" />
                      {section.actionText}
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>)}
        </div>

        <div className="mt-12 p-6 bg-muted rounded-xl border border-border text-center">
          <p className="text-sm text-muted-foreground">
            By providing your phone number to Family Legacy Planning, you consent to receive calls and text messages regarding your inquiries. Standard message and data rates may apply. You may revoke this consent at any time using the methods outlined above.
          </p>
        </div>
      </div>
    </section>;
}